import { useLayoutEffect, useRef, useState } from 'react';
import { animate, motion, useMotionValue, useMotionValueEvent, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { DoubleChevronRightIcon } from 'Asset/icon';

import styles from './styles.module.css';

const THUMB_SIZE = 42;
const PADDING = 7;
const SLIDE_THRESHOLD = 1;
const FEATHER = 6;

const COLOR_THEME = {
  success: {
    bg: 'var(--color-success50)',
    text: 'var(--color-semantic-white)',
    thumb: 'var(--color-success70)',
  },
  error: {
    bg: 'var(--color-error50)',
    text: 'var(--color-semantic-white)',
    thumb: 'var(--color-error70)',
  },
};

const SliderButton = ({ onSlide, label = 'Slide to confirm', disabled, theme = 'success' }) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const maxDragRef = useRef(0);
  const [maxDrag, setMaxDrag] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isPastThreshold, setIsPastThreshold] = useState(false);

  const { bg: bgColor, thumb: thumbColor } = COLOR_THEME[theme];
  maxDragRef.current = maxDrag;

  const confirmText = isPastThreshold ? t('release_to_confirm') : '';

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    setMaxDrag(containerRef.current.getBoundingClientRect().width - THUMB_SIZE - 2 * PADDING);
  }, []);

  const x = useMotionValue(0);
  const progress = useTransform(x, (v) => (maxDragRef.current > 0 ? Math.min(1, Math.max(0, v / maxDragRef.current)) : 0));

  useMotionValueEvent(progress, 'change', (v) => setIsPastThreshold(v >= SLIDE_THRESHOLD));

  const neutralOpacity = useTransform(progress, [0, 0.5, 0.8, 1], [0, 1, 1, 0]);

  const labelOpacity = useTransform(progress, [0, 1], [1, 0]);
  const labelX = useTransform(x, (v) => v * 0.5);
  const labelMask = useTransform(x, (v) => {
    const pos = v * 0.8 + THUMB_SIZE;
    return `linear-gradient(to right, transparent ${pos - 24}px, black ${pos + 24}px)`;
  });

  const textOpacity = useTransform(progress, [0, 1], [0, 1]);
  const textX = useTransform(x, (v) => (v - maxDragRef.current) * 0.5);
  const textMask = useTransform(x, (v) => {
    const pos = PADDING + v * 0.4 + maxDragRef.current * 0.4 + THUMB_SIZE;
    return `linear-gradient(to right, black ${pos - FEATHER}px, transparent ${pos + FEATHER}px)`;
  });

  const playHint = () => {
    if (completed || disabled) return;
    animate(x, [0, 24, 0], { duration: 0.45, times: [0, 0.35, 1] });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    if (x.get() >= maxDragRef.current * SLIDE_THRESHOLD) {
      animate(x, maxDragRef.current, { duration: 0.2 });
      setCompleted(true);
      onSlide?.();
    } else {
      animate(x, 0, { type: 'spring', stiffness: 180, damping: 26 });
    }
  };

  return (
    <motion.div
      ref={containerRef}
      className={styles['slider-button__container']}
      style={{ backgroundColor: bgColor }}
      data-disabled={String(!!disabled)}
      onTap={playHint}
    >
      <motion.div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'var(--color-neutral60)',
          opacity: neutralOpacity,
          pointerEvents: 'none',
        }}
      />

      <motion.div
        className={styles['slider-button__text']}
        style={{
          x: textX,
          opacity: textOpacity,
          maskImage: textMask,
          WebkitMaskImage: textMask,
          fontSize: isPastThreshold ? '18px' : '16px',
          fontWeight: isPastThreshold ? '600' : '400',
          transition: 'font-size 0.2s ease, font-weight 0.2s ease',
        }}
      >
        {confirmText}
      </motion.div>

      <motion.div
        className={styles['slider-button__label']}
        style={{
          x: labelX,
          opacity: labelOpacity,
          maskImage: labelMask,
          WebkitMaskImage: labelMask,
        }}
      >
        {label}
      </motion.div>

      <motion.div
        className={styles['slider-button__thumb']}
        style={{ left: PADDING, x, backgroundColor: thumbColor }}
        data-dragging={String(isDragging)}
        drag={!completed && !disabled ? 'x' : false}
        dragConstraints={{ left: 0, right: maxDrag }}
        dragElastic={0}
        dragMomentum={false}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={handleDragEnd}
      >
        <DoubleChevronRightIcon className={styles['slider-button__icon']} />
      </motion.div>
    </motion.div>
  );
};

export default SliderButton;
